import React, { useState, useEffect } from 'react';
import { Calendar, Power, Key, RefreshCw, AlertCircle, CheckCircle, Globe, UserCheck, Lock, Shield } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const GoogleCalendarIntegration = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [settings, setSettings] = useState({
    client_id: '',
    client_secret: '',
    redirect_uri: `${window.location.origin}/auth/google/callback`,
    sync_days_back: 30,
    sync_days_forward: 90,
    auto_sync_new_employees: false,
    sync_interval_minutes: 60,
    allow_individual_connections: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/settings/integrations/google`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.data) {
        setGlobalEnabled(response.data.enabled || false);
        setSettings({
          client_id: response.data.client_id || '',
          client_secret: response.data.client_secret || '',
          redirect_uri: response.data.redirect_uri || settings.redirect_uri,
          sync_days_back: response.data.sync_days_back || 30,
          sync_days_forward: response.data.sync_days_forward || 90,
          auto_sync_new_employees: response.data.auto_sync_new_employees || false,
          sync_interval_minutes: response.data.sync_interval_minutes || 60,
          allow_individual_connections: response.data.allow_individual_connections !== false
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await axios.put(`${API_BASE}/admin/settings/integrations/google`, {
        enabled: globalEnabled,
        ...settings
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setSaveStatus({ type: 'success', message: 'Google Calendar integration settings saved successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.detail || 'Error saving settings' });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settings.client_id) {
      setSaveStatus({ type: 'error', message: 'Please enter a Client ID first' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    
    setTestStatus('testing');
    try {
      const response = await axios.post(`${API_BASE}/admin/integrations/google/test`, {
        client_id: settings.client_id,
        client_secret: settings.client_secret
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      if (response.data.valid) {
        setTestStatus('success');
        setSaveStatus({ type: 'success', message: 'Connection test successful!' });
      } else {
        setTestStatus('error');
        setSaveStatus({ type: 'error', message: response.data.error || 'Invalid credentials' });
      }
      setTimeout(() => setTestStatus(null), 3000);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setTestStatus('error');
      setSaveStatus({ type: 'error', message: 'Connection test failed' });
      setTimeout(() => setTestStatus(null), 3000);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading integration settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Calendar className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Google Calendar Integration</h3>
              <p className="text-sm text-gray-400">Sync staff schedules with Google Calendar</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={globalEnabled}
              onChange={(e) => setGlobalEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
          </label>
        </div>
      </div>

      {saveStatus && (
        <div className={`mx-6 mt-4 p-3 rounded-lg ${saveStatus.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
          {saveStatus.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
          {saveStatus.message}
        </div>
      )}

      {globalEnabled && (
        <div className="p-6 space-y-6">
          {/* Security Notice */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-400 font-medium">Security Notice</p>
                <p className="text-xs text-gray-400 mt-1">
                  Client secrets are encrypted before storage. Each employee must individually authorize 
                  their Google Calendar connection. Tokens are stored server-side and never exposed to the client.
                </p>
              </div>
            </div>
          </div>

          {/* OAuth Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-white font-medium">
              <Key className="w-4 h-4 text-yellow-400" />
              <span>OAuth Configuration</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Google Client ID</label>
                <input
                  type="text"
                  value={settings.client_id}
                  onChange={(e) => setSettings({ ...settings, client_id: e.target.value })}
                  placeholder="xxxxxxxx.apps.googleusercontent.com"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Google Client Secret</label>
                <input
                  type="password"
                  value={settings.client_secret}
                  onChange={(e) => setSettings({ ...settings, client_secret: e.target.value })}
                  placeholder="••••••••••••••••"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Redirect URI</label>
              <input
                type="text"
                readOnly
                value={settings.redirect_uri}
                className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2 text-gray-400 text-sm cursor-not-allowed font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">Add this URI to your Google Cloud Console  OAuth 2.0 Client IDs</p>
            </div>

            <button
              onClick={handleTestConnection}
              disabled={!settings.client_id || testStatus === 'testing'}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm flex items-center gap-2"
            >
              {testStatus === 'testing' ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : testStatus === 'success' ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : testStatus === 'error' ? (
                <AlertCircle className="w-4 h-4 text-red-400" />
              ) : (
                <Globe className="w-4 h-4" />
              )}
              Test Connection
            </button>
          </div>

          {/* Sync Settings */}
          <div className="space-y-4 border-t border-gray-700 pt-6">
            <div className="flex items-center gap-2 text-white font-medium">
              <RefreshCw className="w-4 h-4 text-blue-400" />
              <span>Synchronization Settings</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Sync Days Back</label>
                <input
                  type="number"
                  value={settings.sync_days_back}
                  onChange={(e) => setSettings({ ...settings, sync_days_back: parseInt(e.target.value) })}
                  min="1"
                  max="365"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">How many past days to sync</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Sync Days Forward</label>
                <input
                  type="number"
                  value={settings.sync_days_forward}
                  onChange={(e) => setSettings({ ...settings, sync_days_forward: parseInt(e.target.value) })}
                  min="1"
                  max="365"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">How many future days to sync</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Sync Interval (minutes)</label>
                <input
                  type="number"
                  value={settings.sync_interval_minutes}
                  onChange={(e) => setSettings({ ...settings, sync_interval_minutes: parseInt(e.target.value) })}
                  min="15"
                  max="1440"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">How often to auto-sync</p>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.auto_sync_new_employees}
                    onChange={(e) => setSettings({ ...settings, auto_sync_new_employees: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-blue-500"
                  />
                  <span className="text-sm text-gray-400">Auto-enable for new employees</span>
                </label>
              </div>
            </div>
          </div>

          {/* Individual Permissions */}
          <div className="space-y-4 border-t border-gray-700 pt-6">
            <div className="flex items-center gap-2 text-white font-medium">
              <UserCheck className="w-4 h-4 text-purple-400" />
              <span>Employee Permissions</span>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-4">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-white">Allow individual connections</span>
                  <p className="text-xs text-gray-500 mt-1">Staff members can connect their own Google Calendar</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.allow_individual_connections}
                  onChange={(e) => setSettings({ ...settings, allow_individual_connections: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                />
              </label>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Lock className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-400 font-medium">How it works</p>
                  <p className="text-xs text-gray-400 mt-1">
                    1. Admin enables integration and configures OAuth<br />
                    2. Admin enables Google Calendar permission per employee (Staff Management)<br />
                    3. Employee connects their Google account from their profile<br />
                    4. Events are synced bidirectionally between Portalith and Google Calendar
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm flex items-center gap-2"
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              Save Configuration
            </button>
          </div>
        </div>
      )}

      {!globalEnabled && (
        <div className="p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">Google Calendar integration is disabled</p>
          <p className="text-sm text-gray-600 mt-1">Enable it above to configure synchronization</p>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarIntegration;